import { useSelector } from 'react-redux'
import { useCallback, useMemo } from 'react'
import { getIn } from 'formik'

import MultiSelectBox from '../../../../../../../../features/components/Form/MultiSelectBox'
import FieldLabelWrapper from '../../../../../../../../features/components/Form/FieldLabelWrapper'
import Field from '../../../../../../../../components/Form/Field'

import { getLoadOptionsHandlerCursorPagination } from '../../../../../../../../features/helpers/componentsHelpers'

import { getGoogleAdManagerCustomTargetingService } from '../../../../../../../../modules/services/rights'

import { selfAccountSelector } from '../../../../../../../../modules/selectors/selfAccounts'
import { GAM_NETWORK_CODE } from '../../../../../../../Multiplatform/Partnerships/RightForms/RightCreate/RightCreateForm/sectionsLists/OnsiteRightCreateSections/GamOnsiteRightCreateSections/fields'
import {
  CUSTOM_TARGETING,
  customTargetingValueOperators,
  LOGICAL_OPERATOR,
  logicalOperatorOptions
} from '../../../fields'

const CustomTargetingKeyValuesSelectors = ({ formik }) => {
  const { values, setFieldValue } = formik

  const selfAccount = useSelector(selfAccountSelector)
  const gamNetworkCode = selfAccount?.[GAM_NETWORK_CODE]

  const formatOptions = useCallback(options => {
    return options.map(option => ({
      ...option,
      value: option.id,
      label: option.name
    }))
  }, [])

  const loadOptions = useMemo(() => {
    return getLoadOptionsHandlerCursorPagination({
      getOptionsService: getGoogleAdManagerCustomTargetingService,
      params: {
        network_code: gamNetworkCode,
        custom_targeting_type: 'KEYS'
      },
      formatOptions: formatOptions
    })
  }, [formatOptions, gamNetworkCode])

  const renderValuesSelect = useCallback(
    id => {
      const loadOptionsValues = getLoadOptionsHandlerCursorPagination({
        getOptionsService: getGoogleAdManagerCustomTargetingService,
        params: {
          network_code: gamNetworkCode,
          custom_targeting_type: 'VALUES',
          custom_targeting_key_id: id
        },
        formatOptions: formatOptions
      })

      const foundIndex = values[CUSTOM_TARGETING].findIndex(item => item.id === id)
      const name = getIn(values, `${CUSTOM_TARGETING}[${foundIndex}].name`)

      return (
        <FieldLabelWrapper label={name} key={id}>
          <MultiSelectBox
            loadOptions={loadOptionsValues}
            value={getIn(values, `${CUSTOM_TARGETING}[${foundIndex}].values`)}
            name={`${CUSTOM_TARGETING}[${foundIndex}].values`}
            setFieldValue={setFieldValue}
            placeholder="Values"
            portaled={true}
          />
          <Field
            formik={formik}
            name={`${CUSTOM_TARGETING}[${foundIndex}].operator`}
            options={customTargetingValueOperators}
            placeholder="Operator"
            preselectFirstOptionValue={true}
          />
        </FieldLabelWrapper>
      )
    },
    [gamNetworkCode, formatOptions, setFieldValue, values, formik]
  )

  const selectKeyHandler = useCallback(
    selectedValues => {
      const formattedValues = selectedValues.map(value => {
        const previouslySelectedValues = values[CUSTOM_TARGETING].find(item => item.id === value.id)?.values

        return {
          ...value,
          values: previouslySelectedValues || [] // Initialize with an empty array for values
        }
      })
      setFieldValue(CUSTOM_TARGETING, formattedValues)
    },
    [setFieldValue, values]
  )

  return (
    <>
      {values[CUSTOM_TARGETING].length > 0 && (
        <FieldLabelWrapper label={'Group logic'}>
          <Field formik={formik} name={LOGICAL_OPERATOR} options={logicalOperatorOptions} />
        </FieldLabelWrapper>
      )}
      {values[CUSTOM_TARGETING].map(item => {
        return renderValuesSelect(item.id)
      })}

      <MultiSelectBox
        loadOptions={loadOptions}
        value={values[CUSTOM_TARGETING]}
        name={CUSTOM_TARGETING}
        setFieldValue={setFieldValue}
        placeholder="Custom key-values"
        disablePlaceholderFormatting={true}
        portaled={true}
        onValueChangeCallback={selectKeyHandler}
        renderSelectedValues={false}
      />
    </>
  )
}

export default CustomTargetingKeyValuesSelectors
